import { CloseOutlined } from "@ant-design/icons";
import { Button, Input, Modal, Progress } from "antd";
import { useState } from "react";
import { v4 as uuidv4 } from "uuid";
import fixWebmDuration from "webm-duration-fix";
import apiFactory from "../../api";
import { CHUNK_SIZE, FILE_STATUS, MESSAGE_STATUS } from "../../config/Constant";
import { useInfoUser } from "../../store/UserStore";
import { formatTimeBySeconds } from "../../utils/formatTime";
import {
  getBase64,
  openNotificationError,
  openNotificationSuccess,
} from "../../utils/Utils";
import "./style.scss";

const RecordStoreModal = ({
  title,
  duration,
  payload,
  changeName,
  onCancel,
  open,
  uploadFile,
}) => {
  const { languageMap } = useInfoUser();
  const [abortController, setAbortController] = useState(null);
  const [progressState, setProgressState] = useState();
  const [isLoading, setIsLoading] = useState(false);

  const sendFile = async () => {
    setIsLoading(true);
    let requestUuid = uuidv4();

    try {
      if (!uploadFile) return null;

      const fixedBlob = await fixWebmDuration(uploadFile);
      const totalChunk = Math.ceil(fixedBlob.size / CHUNK_SIZE);
      const controller = new AbortController();
      setAbortController(controller);

      let totalChunkUploaded = 0;

      for (let i = 0; i < totalChunk; i++) {
        if (controller.signal.aborted) {
          throw new Error("Aborted");
        }

        const chunk = fixedBlob?.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
        const base64Chunk = await getBase64(chunk);

        const item = {
          ...payload,
          name: payload?.name + ".webm",
          base64: base64Chunk,
          totalChunk,
          chunkIndex: i,
          status: FILE_STATUS.READY,
          requestUuid: requestUuid,
        };

        let retryCount = 0;
        let success = false;

        while (retryCount < 3 && !success) {
          const message = await sendFileChunk(item);

          if (message) {
            success = true;
            totalChunkUploaded++;
            setProgressState(
              Math.round((totalChunkUploaded / totalChunk) * 100)
            );
          } else {
            retryCount++;
          }
        }

        if (!success) {
          openNotificationError("Your recording save unsuccessfully");
          return;
        }
      }

      // merge chunk
      if (totalChunkUploaded === totalChunk) {
        const rs = await apiFactory.messageApi.mergeChunk({
          ...payload,
          totalChunk: totalChunk,
          contentType: MESSAGE_STATUS.FILE,
          name: payload?.name + ".webm",
          duration: duration,
          requestUuid: requestUuid,
        });

        if (rs?.status === 200) {
          setTimeout(() => {
            onCancel();
            openNotificationSuccess("Your recording save successfully");
          }, 1000);
        }
      }
    } catch (err) {
      console.log(err);
    }
  };

  const sendFileChunk = async (item) => {
    try {
      const res = await apiFactory.resourceApi.uploadChunk(item);

      if (res?.status === 400) {
        openNotificationError(
          languageMap?.[res?.message] || "Upload failed, try again later"
        );
        return null;
      }

      return {
        ...res?.data,
        fileName: item?.name,
        totalChunk: item?.totalChunk,
        type: item?.type,
      };
    } catch (error) {
      console.error("Error uploading chunk:", error);
      return null;
    }
  };

  const getFileSize = (file) => {
    if (!file?.size) return "0 KB";

    const sizeInMB = file.size / (1024 * 1024);
    const sizeInKB = file.size / 1024;

    return sizeInMB >= 1
      ? `${sizeInMB.toFixed(1)} MB`
      : `${sizeInKB.toFixed(1)} KB`;
  };

  const handleDeleteFile = async () => {
    if (uploadFile && abortController) {
      abortController.abort();
      const res = await apiFactory.messageApi.cancelMessage({
        requestUuid: payload?.requestUuid,
      });

      if (res?.status === 200) {
        openNotificationSuccess(
          languageMap?.[res?.message] || "Cancel upload recording successfully"
        );
        setIsLoading(false);
      }
    }
  };

  return (
    <Modal
      width={450}
      open={open}
      onCancel={onCancel}
      footer={null}
      centered
      maskClosable={false}
      className="custom-confirm-modal"
    >
      <div className="modal-header">
        <div className="modal-title">{title ?? "Xác nhận"}</div>
        <div className="modal-divider" />
      </div>

      <div className="text-base mb-6">
        Video duration: {formatTimeBySeconds(duration)}
      </div>
      <div className="flex flex-row gap-[10px] text-base mb-6">
        <div>Video name: </div>
        <Input
          disabled={isLoading}
          className="flex-1"
          value={payload?.name}
          onChange={changeName}
        />
      </div>
      {isLoading ? (
        <div key={uploadFile?.uid}>
          <div className="mt-[20px]">
            <div className="flex items-center mt-[10px] justify-between">
              <button onClick={handleDeleteFile}>
                <CloseOutlined />
              </button>
              <div>{getFileSize(uploadFile)}</div>
            </div>
            <Progress percent={progressState || 0} className="w-[100%]" />
          </div>
        </div>
      ) : (
        <div className="flex justify-end gap-2">
          <Button
            className="!bg-[#ffffff] !text-black hover:!bg-[#d9d9d9] font-medium"
            onClick={onCancel}
          >
            {languageMap?.["modal.cancel"] ?? "Không"}
          </Button>
          <Button
            className="!bg-[#ff4d4f] !text-white hover:!bg-[#ff7875] font-medium"
            onClick={sendFile}
          >
            {languageMap?.["modal.ok"] ?? "Xóa"}
          </Button>
        </div>
      )}
    </Modal>
  );
};

export { RecordStoreModal };
